import React, { useState, useRef } from "react";
import { ShoppingCart } from "lucide-react";
import { StandardEntityDialog } from "@/components/common/dialogs/StandardEntityDialog";
import AddClientDialog from "@/domains/clients/components/AddClientDialog";
import { useAddOrderDialog } from "@/domains/orders/hooks/useAddOrderDialog";
import { useOrderCreation, createOrderItem } from "@/hooks/orders/useOrderCreation";
import { OrderDialogSections } from "./components/OrderDialogSections";
import { iconStyles } from "@/styles/common-styles";
import { cn } from "@/lib/utils";
import { Order } from '@/types/entities/order';
import { OrderFormData } from "@/domains/orders/hooks/types";
import type { Client } from "@/types/entities/client";
import type { CreateOrderData, CreateOrderItemData } from "@/services/orders/orderCreationService";
import type { Location } from "@/types/entities";
// Debug import removed for production

interface AddOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: CreateOrderData) => void;
  onSuccess?: () => void;
  // Опциональные пропсы для переопределения данных из хука
  clients?: Client[];
  clientsLoading?: boolean;
  currentRentalPoint?: Location | null;
  // Предварительно выбранный клиент
  preselectedClient?: Client | null;
}

export function AddOrderDialog({
  isOpen,
  onClose,
  onSave,
  onSuccess,
  clients: propsClients,
  clientsLoading: propsClientsLoading,
  currentRentalPoint: propsCurrentRentalPoint,
  preselectedClient
}: AddOrderDialogProps) {
  const [isAddClientDialogOpen, setIsAddClientDialogOpen] = useState(false);
  // Предотвращаем двойное нажатие на кнопку сохранения (гоночное состояние до установки isLoading)
  const submitLockRef = useRef(false);
  
  // Новый API создания заказов
  const { createOrder, isCreating: isOrderCreating, error: orderCreationError } = useOrderCreation();
  
  const {
    formData,
    selectedKits,
    setSelectedKits,
    selectedStoreItems,
    setSelectedStoreItems,
    lastAction,
    isStorePurchase,
    handleFieldChange,
    handleInventoryItemChange,
    handleStoreItemChange,
    handleUndoLastAction,
    handleSubmit: originalHandleSubmit,
    isSubmitting: originalIsSubmitting,
    validationErrors: originalValidationErrors,
    clientsData: hookClientsData,
    clientsLoading: hookClientsLoading,
    locations,
    currentRentalPoint: hookCurrentRentalPoint,
    totalAmount,
    inventoryItems,
    inventoryLoading,
    storeItems: availableStoreItems,
    storeLoading,
    rentalKits,
    kitsLoading,
    activeRentalPointId,
    resetFormState
  } = useAddOrderDialog({ 
    onSave: undefined, // Отключаем старый onSave
    onSuccess: undefined, // Отключаем старый onSuccess
    onClose,
    preselectedClientId: preselectedClient?.id // Передаем ID предварительно выбранного клиента
  });

  // Используем пропсы если переданы, иначе значения из хука
  const finalClients = (propsClients as Client[]) ?? (hookClientsData as Client[]) ?? [];
  const finalClientsLoading = propsClientsLoading ?? hookClientsLoading;
  const finalCurrentRentalPoint = (propsCurrentRentalPoint ?? hookCurrentRentalPoint) ?? null;
  
  // Type adaptation: unify locations and currentRentalPoint types for OrderDialogSections props
  // The hook returns a compatible shape, and the sections consume only id/name fields.
  const locationsForSections = (locations ?? []) as unknown as Location[];
  const currentRentalPointForSections = (finalCurrentRentalPoint ?? null) as unknown as Location | null;

  // Эффект для установки предварительно выбранного клиента
  React.useEffect(() => {
    if (preselectedClient && isOpen && !formData.clientId) {
      console.log('[AddOrderDialog] Setting preselected client:', preselectedClient.fullName || preselectedClient.companyName);
      handleFieldChange('clientId', preselectedClient.id);
    }
  }, [preselectedClient, isOpen, formData.clientId, handleFieldChange]);

  // Сброс состояния формы при закрытии диалога
  React.useEffect(() => {
    if (!isOpen) {
      console.log('[AddOrderDialog] Dialog closed, resetting form state');
      resetFormState();
      // Сбрасываем лок при закрытии диалога
      submitLockRef.current = false;
    }
  }, [isOpen, resetFormState]);

  const handleNewClientCreated = (newClientId: string) => {
    console.log('[AddOrderDialog] New client created:', newClientId);
    handleFieldChange('clientId', newClientId);
    setIsAddClientDialogOpen(false);
  };

  // Новая функция создания заказа с использованием нового API
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Блокируем повторную отправку, если уже идёт создание
      if (submitLockRef.current) {
        console.warn('[AddOrderDialog] Duplicate submit prevented');
        return;
      }
      submitLockRef.current = true;

      // Подготовка данных для создания заказа
      const orderItems: CreateOrderItemData[] = [];

      // Подсчёт количества дней аренды для корректного расчёта суммы
      let rentalDays = 1;
      if (formData.startDate && formData.endDate) {
        try {
          const startTime = formData.startDate instanceof Date
            ? formData.startDate.getTime()
            : new Date(formData.startDate).getTime();
          const endTime = formData.endDate instanceof Date
            ? formData.endDate.getTime()
            : new Date(formData.endDate).getTime();
          if (!isNaN(startTime) && !isNaN(endTime) && endTime > startTime) {
            const diff = endTime - startTime;
            rentalDays = Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
          }
        } catch {
          rentalDays = 1;
        }
      }

      // Добавляем товары инвентаря (учитываем стоимость за сутки и количество дней)
      formData.inventoryItems.forEach(item => {
        const inventoryItem = inventoryItems?.find(inv => inv.id === item.id);
        if (inventoryItem) {
          // Используем стандартизированные поля цен согласно STYLEGUIDE.md
          const dailyPrice = inventoryItem.dailyPrice ?? inventoryItem.price ?? 0;
          // В helper totalPrice = unitPrice * quantity, поэтому включаем дни в unitPrice
          const unitPriceWithDays = (dailyPrice as number) * rentalDays;
          orderItems.push(createOrderItem.fromInventory(
            item.id,
            item.quantity,
            unitPriceWithDays
          ));
        }
      });

      // Добавляем товары магазина
      selectedStoreItems.forEach(item => {
        const storeItem = availableStoreItems?.find(store => store.id === item.id);
        if (storeItem) {
          orderItems.push(createOrderItem.fromStore(
            item.id,
            item.quantity,
            storeItem.price || 0
          ));
        }
      });

      // Подготовка данных заказа
      const orderData: CreateOrderData = {
        clientId: formData.clientId,
        // Ensure rental point is always populated: prefer form value, then resolved active, then current context, then first location
        rentalPointId: formData.rentalPointId || activeRentalPointId || (finalCurrentRentalPoint?.id as string) || (locations?.[0]?.id as string),
        returnPointId: formData.returnPointId || undefined,
        startDate: formData.startDate?.toISOString(),
        endDate: formData.endDate?.toISOString(),
        status: formData.status,
        paymentStatus: formData.paymentStatus,
        tariffId: formData.tariffId || undefined,
        paymentMethodId: formData.paymentMethodId || undefined,
        promoCode: formData.promoCode || undefined,
        discount: formData.discount || 0,
        depositAmount: formData.depositAmount || 0,
        contractGenerated: Boolean(formData.generateContract),
        notes: formData.notes || undefined,
        items: orderItems
      };

      console.log('[AddOrderDialog] Creating order with new API:', orderData);

      // Создание заказа через новый API
      const createdOrder = await createOrder(orderData);

      console.log('[AddOrderDialog] Order created successfully:', createdOrder.id);

      // Вызов колбэков если переданы (onSave отключен во избежание дублей создания заказа)
      // if (onSave) {
      //   await onSave(createdOrder);
      // }

      if (onSuccess) {
        onSuccess();
      }

      // Форма уже сброшена в хуке при успешном создании
      onClose();
      // Сбрасываем лок после успешного завершения
      submitLockRef.current = false;
    } catch (error) {
      console.error('[AddOrderDialog] Error creating order:', error);
      // Ошибка уже обработана в useOrderCreation
      // Разблокируем отправку при ошибке, чтобы пользователь мог повторить попытку
      submitLockRef.current = false;
    }
  };

  // Объединяем состояния загрузки и ошибок
  const isSubmitting = isOrderCreating || originalIsSubmitting;
  const validationErrors = orderCreationError ? [orderCreationError] : originalValidationErrors;

  return (
    <>
      <StandardEntityDialog
        title="Создать заказ"
        icon={<ShoppingCart className={cn(iconStyles.medium, "text-primary")} />}
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleSubmit}
        isLoading={isSubmitting}
        validationErrors={validationErrors}
        size="standard"
      >
        <OrderDialogSections
          formData={formData}
          selectedKits={selectedKits}
          selectedStoreItems={selectedStoreItems}
          clients={finalClients}
          clientsLoading={finalClientsLoading}
          currentRentalPoint={currentRentalPointForSections}
          locations={locationsForSections}
          totalAmount={totalAmount}
          lastAction={lastAction}
          inventoryItems={inventoryItems}
          inventoryLoading={inventoryLoading}
          storeItems={availableStoreItems}
          storeItemsLoading={storeLoading}
          rentalKits={rentalKits}
          kitsLoading={kitsLoading}
          isStorePurchase={isStorePurchase}
          isEditMode={false}
          onClientChange={(clientId) => handleFieldChange('clientId', clientId)}
          onAddClient={() => setIsAddClientDialogOpen(true)}
          onDateChange={(field, value) => handleFieldChange(field as keyof OrderFormData, value)}
          onKitsChange={setSelectedKits}
          onInventoryItemChange={handleInventoryItemChange}
          onStoreItemChange={handleStoreItemChange}
          onUndoLastAction={handleUndoLastAction}
          onFieldChange={handleFieldChange}
        />
      </StandardEntityDialog>

      {/* Debug component removed for production */}

      <AddClientDialog
        open={isAddClientDialogOpen}
        onOpenChange={setIsAddClientDialogOpen}
        onClientCreated={handleNewClientCreated}
      />
    </>
  );
}
